import React, { useEffect, useState } from 'react';
import { getMasterData } from '../../../api/masterDataApi';
import { useNavigate, useLocation } from 'react-router-dom';
// Add this import to check auth
import { CheckCircle2, Check, Minus, ChevronDown } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useBilling } from '../../../context/BillingContext';
import { upgradeSubscription, sendContactMessage, fetchPlans, activateFreePlan, Plan } from '../../../api/billingApi';
import { fmtQuantity, isQuantityOff, fetchPaymentMethods, offersPayPal } from '../../../api/billingApi';
import { COMPARISON, quantityCell } from '../pricingComparison';
import { bestSavingPct } from '../planPrices';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './PricingPage.module.css';
import MarketingShell from '../../marketing/MarketingShell';

const FEATURE_LABELS: { key: keyof Plan['features']; label: string }[] = [
  { key: 'user_seats', label: 'User seats' },
  { key: 'jurisdictions', label: 'Jurisdictions' },
  { key: 'ai_summaries_per_month', label: 'AI queries/mo' },
  { key: 'documents_per_month', label: 'Document analyses/mo' },
  { key: 'multi_jurisdiction', label: 'Multi-jurisdiction tracking' },
  { key: 'custom_alerts', label: 'Custom alerts' },
  { key: 'export_reporting', label: 'Export & reporting' },
  { key: 'advanced_ai', label: 'Advanced AI analysis' },
  { key: 'api_access', label: 'API access' },
  { key: 'team_collaboration', label: 'Team collaboration' },
  { key: 'sso', label: 'SSO / SCIM' },
  { key: 'predictive_analytics', label: 'Early Warning System' },
  { key: 'dedicated_support', label: 'Dedicated CSM' },
];

/** A plan the catalogue prices at $0 (not a contact-sales plan). */
const isFreePlan = (p: Plan): boolean => !p.contact_sales && !Number(p.monthly_price_usd) && !Number(p.annual_price_usd);

/** Replaced with the methods the checkout really offers; see ``paymentAnswer``. */
const PAYMENT_ANSWER = '__payment_methods__';
/** Names only what a customer can actually pay with. PayPal is withdrawn until
 *  its live credentials are in place, and a public page that promises it would
 *  be promising a button that is not there. */
export const paymentAnswer = (paypal: boolean): string =>
  `We accept all major credit/debit cards (Visa, Mastercard, Amex)${paypal ? ' and PayPal' : ''}.`;

const FAQ = [
  { q: "How does the 14-day free trial work?", a: "You get full Professional-tier access for 14 days. No credit card required to start. On Day 10 we'll show you upgrade options; on Day 14 your account enters read-only mode until you subscribe." },
  { q: "What counts as a jurisdiction?", a: "Each country, US state, or distinct regulatory body counts as one jurisdiction. International regions (EU, ASEAN) count as one each." },
  { q: "Can I change plans?", a: "Yes, upgrade or downgrade any time. Pro-rated credits are applied automatically." },
  { q: "What payment methods are accepted?", a: PAYMENT_ANSWER },
  { q: "Is there a contract or cancellation fee?", a: "No contracts. Cancel anytime. Annual plans are refunded pro-rata within 30 days." },
];



const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Currency symbol from billing configuration (SRS #86); '$' until it loads.
  // SRS #52: the plan the visitor clicked before signing up must be the plan
  // they end up on. The card handlers store it; the ?plan= query set by the
  // login redirect restores it, so a stale choice from an earlier visit
  // (e.g. Law Firm) can no longer become a Startup user's trial.
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get('plan');
    if (wanted && /^[a-z_]+$/.test(wanted)) localStorage.setItem('selected_trial_plan', wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // False until the backend says otherwise: a failed call must not promise a
  // payment method the checkout does not offer.
  const [paypalOffered, setPaypalOffered] = useState(false);
  useEffect(() => { fetchPaymentMethods().then((m) => setPaypalOffered(offersPayPal(m))).catch(() => {}); }, []);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  useEffect(() => { getMasterData().then((m) => setCurrencySymbol(m.billing.currency_symbol || m.billing.currency)).catch(() => {}); }, []);
  const { user } = useAuth();
  const isAuthenticated = !!user;
  const { subscription, refresh } = useBilling();
  const [annual, setAnnual] = useState(true);
  const [plans, setPlans] = useState<Plan[]>([]);
  const bestSaving = bestSavingPct(plans);   // the badge reads the catalogue, not a typed figure
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [changingPlan, setChangingPlan] = useState<string | null>(null);
  const [changeSuccess, setChangeSuccess] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  const openContact = (subject: string) => {
    setContactSubject(subject);
    setContactMessage('');
    setContactSent(false);
    setContactError(null);
    setContactOpen(true);
  };

  useEffect(() => {
    fetchPlans()
      .then(setPlans)
      .catch(() => {/* fallback: empty */});
  }, []);

  // The admin catalogue is the only source: every plan marked "shown" is a
  // card, in the catalogue's order. Nothing about a plan is hard-coded here.
  const shownPlans = plans
    .filter(p => p.show_on_pricing !== false && !p.disabled)
    .sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100));
  const currentPlan = subscription ? plans.find(p => p.tier === subscription.plan_tier) : undefined;
  const paidActive = !!subscription && subscription.status === 'active' && !!currentPlan && !isFreePlan(currentPlan);

  const getCtaLabel = (planTier: string, isLoading: boolean, free = false): string => {
    if (isLoading) return 'Changing...';
    if (changeSuccess === planTier) return 'Done!';
    if (free) {
      if (subscription?.plan_tier === planTier && subscription.status === 'active') return 'Current Plan';
      if (paidActive) return 'Cancel your plan to switch';
      return 'Get started free';
    }
    if (!isAuthenticated) return 'Start free trial';
    if (location.state?.changeOnly) {
      return subscription?.plan_tier === planTier ? 'Proceed with Plan' : 'Change Plan';
    }
    if (!subscription) return 'Start Free Trial';
    if (subscription.status === 'trialing') return 'Choose Plan';
    if (subscription.plan_tier === planTier) return 'Current Plan';
    return 'Upgrade';
  };

  const isCtaDisabled = (planTier: string): boolean => {
    if (!!changingPlan) return true;
    if (!location.state?.changeOnly && subscription && subscription.status === 'active' && subscription.plan_tier === planTier) return true;
    return false;
  };

  const price = (plan: Plan): string => {
    if (isFreePlan(plan)) return `${currencySymbol}0`;
    if (!plan.monthly_price_usd) return 'Custom';
    if (annual && plan.annual_monthly_equivalent) {
      return `${currencySymbol}${parseFloat(plan.annual_monthly_equivalent).toFixed(0)}`;
    }
    return `${currencySymbol}${parseFloat(plan.monthly_price_usd).toFixed(0)}`;
  };

  const formatVal = (key: string, v: string | number | boolean): string => {
    if (typeof v === 'boolean') return v ? 'Included' : 'Not included';
    return fmtQuantity(key, v);   // seats 0 → "unlimited", never "0 user seats"
  };

  const finish = async (tier: string) => {
    localStorage.removeItem('selected_trial_plan');
    await refresh();
    setChangeSuccess(tier);
    setTimeout(() => { setChangeSuccess(null); navigate('/billing'); }, 1200);
  };

  const chooseFree = async (plan: Plan) => {
    if (!isAuthenticated) {
      localStorage.setItem('selected_trial_plan', plan.tier);
      navigate(`/login?next=/pricing&plan=${plan.tier}`);
      return;
    }
    setChangingPlan(plan.tier);
    try {
      await activateFreePlan(plan.tier);
      await finish(plan.tier);
    } catch (e) {
      setChangeSuccess(null);
      alert(e instanceof Error && e.message ? e.message : 'Could not switch to the free plan.');
    } finally {
      setChangingPlan(null);
    }
  };

  return (
    <MarketingShell>
      <div className={styles.page}>
      {/* Hero — plain h1 to keep full color control on the dark background */}
      <section className={styles.hero}>
        <span className={styles.heroBadge}>
          <span className={styles.heroBadgeDot} aria-hidden="true" />
          SIMPLE, TRANSPARENT PRICING
        </span>
        <h1 className={styles.heroTitle}>
          Regulatory intelligence<br />for <span className={styles.heroTitleAccent}>every team</span>
        </h1>
        <p className={styles.heroSub}>14-day free trial · No credit card required · Cancel anytime</p>
        {location.state?.reason && (
          <p role="status" style={{ margin: '0 auto 12px', maxWidth: 560, padding: '8px 14px', borderRadius: 8, background: 'var(--ds-warning-bg)', border: '1px solid #fedf89', color: 'var(--ds-warning-text)', fontSize: 14 }}>
            {location.state.reason} — pick a plan that covers it.
          </p>
        )}

        {/* Billing toggle — dark track, white thumb slides between Monthly/Annual; indigo track when Annual */}
        <div className={styles.toggleRow}>
          <span className={`${styles.toggleLabel} ${!annual ? styles.toggleLabelOn : ''}`}>Monthly</span>
          <button
            type="button"
            className={`${styles.togglePill} ${annual ? styles.togglePillAnnual : ''}`}
            aria-label="Toggle billing cycle"
            aria-pressed={annual}
            onClick={() => setAnnual((a) => !a)}
          >
            <span className={styles.toggleThumb} aria-hidden="true" />
          </button>
          <span className={`${styles.toggleLabel} ${annual ? styles.toggleLabelOn : ''}`}>Annual</span>
          {annual && bestSaving !== null && <span className={styles.saveBadge}>SAVE {bestSaving}%</span>}
        </div>
      </section>

      {/* Plan cards — one per catalogue plan marked "shown" */}
      <section className={styles.cardsSection}>
        {shownPlans.length === 0 && (
          <p className={styles.loadingPlans} role="status">Loading plans…</p>
        )}
        <div className={styles.cards}>
          {shownPlans.map((plan) => {
            const free = isFreePlan(plan);
            const custom = plan.contact_sales && !plan.monthly_price_usd;
            return (
            <div
              key={plan.tier}
              className={`${styles.card} ${plan.is_popular ? styles.popular : ''} ${plan.contact_sales ? styles.enterprise : ''}`}
              data-testid={`plan-card-${plan.tier}`}
            >
              {plan.is_popular && <div className={styles.popularBadge}>Most Popular</div>}
              <h2 className={styles.planName}>{plan.display_name}</h2>
              <p className={styles.planDesc}>{plan.description}</p>
              <div className={styles.priceRow}>
                <span className={styles.priceAmount}>{custom ? 'Custom' : price(plan)}</span>
                {!custom && <span className={styles.priceSuffix}>/mo</span>}
              </div>
              {custom && <div className={styles.billedAnnually}>Priced for your organisation</div>}
              {free && <div className={styles.billedAnnually}>No card needed · never expires</div>}
              {!free && !custom && annual && plan.annual_price_usd && (
                <div className={styles.billedAnnually}>
                  Billed {currencySymbol}{parseFloat(plan.annual_price_usd).toLocaleString()}/yr
                </div>
              )}
              {plan.contact_sales ? (
                <button className={styles.ctaBtn} onClick={() => openContact(`${plan.display_name} Inquiry`)}>
                  Contact Team
                </button>
              ) : free ? (
                <button
                  className={styles.ctaBtn}
                  onClick={() => chooseFree(plan)}
                  disabled={!!changingPlan || paidActive || (subscription?.plan_tier === plan.tier && subscription.status === 'active')}
                >
                  {getCtaLabel(plan.tier, changingPlan === plan.tier, true)}
                </button>
              ) : (
              <button
                className={`${styles.ctaBtn} ${plan.is_popular ? styles.ctaPopular : ''}`}
                onClick={async () => {
                  if (!isAuthenticated) {
                    localStorage.setItem('selected_trial_plan', plan.tier);
                    navigate(`/login?next=/pricing&plan=${plan.tier}`);
                  } else if (!subscription) {
                    // Start free trial for this plan only on explicit button click
                    try {
                      setChangingPlan(plan.tier);
                      localStorage.setItem('selected_trial_plan', plan.tier);
                      const api = await import('../../../api/billingApi');
                      await api.startTrial(plan.tier);
                      await finish(plan.tier);
                    } catch {
                      setChangeSuccess(null);
                      alert('Failed to start free trial.');
                    } finally {
                      setChangingPlan(null);
                    }
                  } else if (subscription && location.state?.changeOnly && subscription.status === 'trialing') {
                    // Only a trial switches tier for free; an active subscription changing plan goes through checkout.
                    setChangingPlan(plan.tier);
                    try {
                      await upgradeSubscription(plan.tier, annual ? 'annual' : 'monthly');
                      await finish(plan.tier);
                    } catch {
                      setChangeSuccess(null);
                      alert('Failed to change plan.');
                    } finally {
                      setChangingPlan(null);
                    }
                  } else {
                    navigate(`/billing/checkout?plan=${plan.tier}&cycle=${annual ? 'annual' : 'monthly'}`);
                  }
                }}
                disabled={isCtaDisabled(plan.tier)}
              >
                {getCtaLabel(plan.tier, changingPlan === plan.tier)}
              </button>
              )}
              <ul className={styles.featureList}>
                {FEATURE_LABELS.slice(0, 7).map(({ key, label }) => {
                  const val = plan.features[key];
                  // A zero allowance is as absent as a switched-off flag: "no document
                  // analyses/mo" beside a tick reads as if the plan included them.
                  const isOff = typeof val === 'boolean' ? !val : isQuantityOff(key, val);
                  return (
                  <li key={key} className={`${styles.featureItem} ${isOff ? styles.featureItemOff : ''}`}>
                    <span className={isOff ? styles.dash : styles.check} aria-hidden="true">
                      {isOff ? <Minus size={16} strokeWidth={2.5} /> : <Check size={16} strokeWidth={2.5} />}
                    </span>
                    <span>
                      {typeof plan.features[key] !== 'boolean'
                        ? `${formatVal(key, plan.features[key])} ${label.toLowerCase()}`
                        : label}
                    </span>
                  </li>
                  );
                })}
              </ul>
            </div>
            );
          })}
        </div>
      </section>

      {/* Compare plans — the client's plan sheet; quotas read live from the catalogue */}
      {shownPlans.length > 0 && (
      <section className={styles.compareSection}>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <Eyebrow tone="default">EVERYTHING IN DETAIL</Eyebrow>
        </div>
        <PageHeading
          accentWord="plans"
          accentTone="orange"
          align="center"
          size="h2"
          className={styles.sectionTitle}
        >
          Compare plans
        </PageHeading>
        <div className={styles.compareScroll}>
          <table className={styles.compareTable} aria-label="Plan comparison">
            <thead>
              <tr>
                <th scope="col" className={styles.compareFeatureCol}>Feature</th>
                {shownPlans.map(p => (
                  <th scope="col" key={p.tier}>
                    <div>{p.display_name}</div>
                    <div className={styles.compareHeadPrice}>
                      {p.contact_sales && !p.monthly_price_usd ? 'Custom' : `${price(p)}/mo`}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map(group => (
                <React.Fragment key={group.title}>
                  <tr className={styles.compareGroup}>
                    <th scope="rowgroup" colSpan={shownPlans.length + 1}>{group.title}</th>
                  </tr>
                  {group.rows.map(r => (
                    <tr key={r.label}>
                      <th scope="row" className={styles.compareFeatureCol}>{r.label}</th>
                      {shownPlans.map(p => {
                        const cell = 'quantity' in r
                          ? quantityCell(p.features[r.quantity], r.suffix)
                          : (r.values[p.tier] ?? '—');
                        const tone = cell === '✓' ? styles.cellYes : cell === '—' ? styles.cellNo : styles.cellText;
                        return (
                          <td key={p.tier} className={tone}>
                            {cell === '✓' ? <Check size={16} strokeWidth={2.5} aria-label="Included" /> : cell === '—' ? <span aria-label="Not included">—</span> : cell}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section className={styles.faqSection}>
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <Eyebrow tone="default">GOT QUESTIONS?</Eyebrow>
        </div>
        <PageHeading
          accentWord="questions"
          accentTone="orange"
          align="center"
          size="h2"
          className={styles.sectionTitle}
        >
          Frequently asked questions
        </PageHeading>
        <div className={styles.faqList}>
          {FAQ.map((f) => (f.a === PAYMENT_ANSWER ? { ...f, a: paymentAnswer(paypalOffered) } : f)).map((item, i) => (
            <div key={i} className={styles.faqItem}>
              <button
                className={styles.faqQ}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
              >
                <span className={styles.faqQText}>{item.q}</span>
                <span className={`${styles.faqArrow} ${openFaq === i ? styles.faqArrowOpen : ''}`} aria-hidden="true">
                  <ChevronDown size={18} strokeWidth={2.25} />
                </span>
              </button>
              {openFaq === i && <div className={styles.faqA}>{item.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Modal */}
      {contactOpen && (
        <div
          onClick={() => setContactOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--color-bg-elevated, #fff)', border: '1px solid var(--color-border-subtle, var(--ds-border))', borderRadius: 14, padding: 32, maxWidth: 460, width: '100%', color: 'var(--color-text, var(--ds-text))', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800 }}>Contact Our Team</h3>
            <p style={{ margin: '0 0 18px', color: 'var(--color-text-muted, var(--ds-text-muted))', fontSize: 14 }}>Send us a message and we'll get back to you shortly.</p>
            {contactSent ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ marginBottom: 10 }}><CheckCircle2 size={40} aria-hidden /></div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Message sent!</div>
                <div style={{ color: 'var(--color-text-muted, var(--ds-text-muted))', fontSize: 13, marginTop: 4 }}>We'll respond within 1–2 business days.</div>
                <button
                  style={{ marginTop: 18, padding: '10px 28px', background: '#101828', color: 'var(--ds-text-on-dark)', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                  onClick={() => setContactOpen(false)}
                >Close</button>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setContactSending(true); setContactError(null);
                try {
                  await sendContactMessage({ subject: contactSubject, message: contactMessage });
                  setContactSent(true);
                } catch {
                  setContactError('Failed to send. Please try again.');
                } finally { setContactSending(false); }
              }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--color-text-muted, var(--ds-text-secondary))' }}>Subject</label>
                  <input
                    type="text" required value={contactSubject}
                    onChange={e => setContactSubject(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--color-border-subtle, var(--ds-border))', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, var(--ds-text))' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--color-text-muted, var(--ds-text-secondary))' }}>Message</label>
                  <textarea
                    required value={contactMessage}
                    onChange={e => setContactMessage(e.target.value)}
                    placeholder="Describe your requirements or questions…"
                    rows={5}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--color-border-subtle, var(--ds-border))', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, var(--ds-text))' }}
                  />
                </div>
                {contactError && <div style={{ color: 'var(--ds-danger-text)', fontSize: 13, marginBottom: 10 }}>{contactError}</div>}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--color-border-subtle, var(--ds-border))', borderRadius: 8, fontSize: 14, cursor: 'pointer', color: 'var(--color-text, var(--ds-text))' }} onClick={() => setContactOpen(false)}>Cancel</button>
                  <button type="submit" style={{ flex: 1, padding: 10, background: '#101828', color: 'var(--ds-text-on-dark)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: contactSending ? 0.7 : 1 }} disabled={contactSending}>
                    {contactSending ? 'Sending…' : 'Send Message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      </div>
    </MarketingShell>
  );
};

export default PricingPage;
